import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Key, User, Shield, Info, Linkedin, Loader, Unplug } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { asArray } from '../lib/safeData';

export default function Settings() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(searchParams.get('tab') === 'social' ? 'social' : 'profile');
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [identities, setIdentities] = useState([]);
  const [socialLoading, setSocialLoading] = useState(true);

  const loadLinkedIn = async () => {
    setSocialLoading(true);
    try {
      const [statusResponse, identitiesResponse] = await Promise.all([
        api.get('/linkedin/status'),
        api.get('/linkedin/identities'),
      ]);
      setLinkedinStatus(statusResponse.data);
      setIdentities(asArray(identitiesResponse.data));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load LinkedIn settings');
    } finally {
      setSocialLoading(false);
    }
  };

  useEffect(() => {
    loadLinkedIn();
    const callbackError = searchParams.get('linkedin_error');
    if (callbackError) toast.error(`LinkedIn connection failed: ${callbackError}`);
    if (searchParams.get('linkedin') === 'connected') toast.success('LinkedIn connected');
  }, []);

  const connectLinkedIn = async () => {
    try {
      const response = await api.get('/linkedin/auth-url');
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to start LinkedIn connection');
    }
  };

  const switchLinkedInAccount = async () => {
    if (!window.confirm('Switch the LinkedIn account used for future publishing? Your current connection remains active unless the new login succeeds.')) return;
    try {
      const response = await api.get('/linkedin/auth-url?switch_account=true');
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to start LinkedIn account switching');
    }
  };

  const disconnectLinkedIn = async () => {
    if (!window.confirm('Disconnect LinkedIn from Mayr Ops? Scheduled posts will fail until it is reconnected.')) return;
    try {
      await api.delete('/linkedin/disconnect');
      toast.success('LinkedIn disconnected');
      await loadLinkedIn();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect LinkedIn');
    }
  };

  const setDefaultIdentity = async (identityId) => {
    try {
      await api.post(`/linkedin/identities/${identityId}/set-default`);
      await loadLinkedIn();
      toast.success('Default publishing identity updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update publishing identity');
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Account and system configuration</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar nav */}
        <div className="glass rounded-xl border border-white/5 p-3 h-fit">
          {[
            { id: 'profile', icon: User, label: 'Profile' },
            { id: 'social', icon: Linkedin, label: 'Social Publishing' },
            { id: 'security', icon: Shield, label: 'Security' },
            { id: 'about', icon: Info, label: 'About' },
          ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === id ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'}`}
              data-testid={`settings-nav-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-4">
          {activeSection === 'profile' && (
            <div className="glass rounded-xl border border-white/5 p-6 space-y-5" data-testid="profile-section">
              <h2 className="font-heading font-bold text-foreground">Profile</h2>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
                  <User className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <div className="font-heading font-bold text-foreground">{user?.name}</div>
                  <div className="text-sm text-muted-foreground font-mono">{user?.email}</div>
                  {user?.is_owner && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary font-mono mt-1 inline-block">Owner</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm pt-2">
                {[
                  { label: 'User ID', value: user?.id },
                  { label: 'Role', value: user?.is_owner ? 'Owner' : 'Member' },
                  { label: 'Email', value: user?.email },
                  { label: 'Member since', value: user?.created_at ? new Date(user.created_at).toLocaleDateString('en-AT') : '—' },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div className="text-xs text-muted-foreground">{label}</div>
                    <div className="text-foreground font-mono text-xs mt-0.5 break-all">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'social' && (
            <div className="glass rounded-xl border border-white/5 p-6 space-y-5" data-testid="social-section">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-heading font-bold text-foreground">LinkedIn Publishing</h2>
                  <p className="text-xs text-muted-foreground mt-1">Connect your personal profile. Company-page publishing can be enabled after LinkedIn approves organization access.</p>
                </div>
                {socialLoading ? <Loader className="w-5 h-5 animate-spin text-primary" /> : linkedinStatus?.connected ? (
                  <div className="flex items-center gap-2">
                    <button onClick={switchLinkedInAccount} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-primary/30 text-primary text-xs"><Linkedin className="w-3.5 h-3.5" /> Switch Account</button>
                    <button onClick={disconnectLinkedIn} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-destructive/30 text-destructive text-xs"><Unplug className="w-3.5 h-3.5" /> Disconnect</button>
                  </div>
                ) : (
                  <button onClick={connectLinkedIn} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0A66C2] text-white text-sm font-medium"><Linkedin className="w-4 h-4" /> Connect LinkedIn</button>
                )}
              </div>

              {linkedinStatus?.expired && <div className="p-3 rounded-lg border border-destructive/20 bg-destructive/10 text-destructive text-xs">Your LinkedIn token expired. Reconnect before publishing.</div>}
              {linkedinStatus?.connected && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs text-status-up"><span className="w-2 h-2 rounded-full bg-status-up" /> Connected</div>
                  {identities.length === 0 ? <p className="text-sm text-muted-foreground">No publishing identities were returned by LinkedIn. Reconnect and verify application permissions.</p> : identities.map(identity => (
                    <div key={identity.id} className="flex items-center justify-between gap-4 p-4 rounded-lg bg-secondary/20 border border-white/5">
                      <div>
                        <div className="text-sm font-medium text-foreground">{identity.name}</div>
                        <div className="text-xs text-muted-foreground capitalize">{identity.type}</div>
                      </div>
                      {identity.is_default ? <span className="text-xs px-2 py-1 rounded-full bg-status-up/10 text-status-up border border-status-up/20">DEFAULT</span> : <button onClick={() => setDefaultIdentity(identity.id)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-muted-foreground hover:text-foreground">Make default</button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeSection === 'security' && (
            <div className="glass rounded-xl border border-white/5 p-6 space-y-5" data-testid="security-section">
              <h2 className="font-heading font-bold text-foreground">Security</h2>
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-secondary/20 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-foreground">Credential Encryption</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-status-up/10 text-status-up border border-status-up/20 font-mono">ACTIVE</span>
                  </div>
                  <p className="text-xs text-muted-foreground">All credentials stored in the App Registry are encrypted using Fernet symmetric encryption (AES-128 CBC). Keys are never logged or exposed in API responses — only masked values are shown.</p>
                </div>
                <div className="p-4 rounded-lg bg-secondary/20 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <Key className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-foreground">Session Management</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-status-up/10 text-status-up border border-status-up/20 font-mono">ACTIVE</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Sessions use secure httpOnly cookies with 7-day expiry. Logout invalidates the session server-side. All auth endpoints are rate-limited by the infrastructure.</p>
                </div>
                <div className="p-4 rounded-lg bg-secondary/20 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-status-degraded" />
                    <span className="text-sm font-medium text-foreground">Audit Trail</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-status-up/10 text-status-up border border-status-up/20 font-mono">ACTIVE</span>
                  </div>
                  <p className="text-xs text-muted-foreground">All automation runs, health checks, and connector actions are logged in the Activity Feed with timestamps.</p>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'about' && (
            <div className="glass rounded-xl border border-white/5 p-6 space-y-5" data-testid="about-section">
              <h2 className="font-heading font-bold text-foreground">About Mayr Ops</h2>
              <div className="space-y-4 text-sm text-muted-foreground">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Version', value: '1.0.0 MVP' },
                    { label: 'Stack', value: 'FastAPI + React' },
                    { label: 'Database', value: 'SQLite (SQLAlchemy)' },
                    { label: 'Migration', value: 'Postgres-ready' },
                    { label: 'Connectors', value: '3 (pro_email, email_extractor, generic)' },
                    { label: 'Deployment', value: 'Docker-compatible' },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <div className="text-xs text-muted-foreground mb-0.5">{label}</div>
                      <div className="text-foreground font-mono text-xs">{value}</div>
                    </div>
                  ))}
                </div>
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/10 text-xs">
                  <p className="font-medium text-foreground mb-1">Migration to Postgres</p>
                  <p className="text-muted-foreground">To migrate from SQLite to Postgres, update DATABASE_URL in database.py to <span className="font-mono text-primary">postgresql+asyncpg://user:pass@host/db</span> and run with alembic migrations. The SQLAlchemy models are 100% compatible.</p>
                </div>
                <div className="p-4 rounded-lg bg-secondary/20 border border-white/5 text-xs">
                  <p className="font-medium text-foreground mb-1">Connector Framework</p>
                  <p>All connectors live in <span className="font-mono text-primary">backend/connectors/</span>. See the Connector Developer Guide for instructions on adding new integrations.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
